/**
 * Spec §8 A17 — pre-pay visibility for mixed-cart refund / cancellation terms.
 * Spec §8 A18 — when platform legal floors CONTENT is present, clamp less-favorable
 * customer copy to PO-supplied floor text (STOP-A18-CONTENT when floors empty).
 */

export type MixedCartFulfillmentClass = 'ORDINARY' | 'PREPARED_TIME_SENSITIVE';

export interface MixedCartLineTermInput {
  readonly productId: number;
  readonly productName: string;
  readonly fulfillmentClass: MixedCartFulfillmentClass;
}

export interface MixedCartLegalFloorPayload {
  readonly customerFacingCopy?: string;
}

export interface MixedCartLegalFloor {
  readonly key: string;
  readonly minAutomaticRefundRule?:
    | 'NO_AUTOMATIC_REFUND'
    | 'FULL_UNTIL_PREP_STARTED'
    | 'FULL_REFUND'
    | null;
  readonly payload?: MixedCartLegalFloorPayload;
}

export interface MixedCartLegalFloorsInput {
  readonly floors: readonly MixedCartLegalFloor[];
}

export interface MixedCartRefundTermsViewModel {
  readonly isMixed: boolean;
  readonly ordinaryCount: number;
  readonly preparedCount: number;
  readonly summary: string;
  readonly lineNotes: readonly {
    readonly productId: number;
    readonly productName: string;
    readonly termLabel: string;
  }[];
  /** True when A18 floor CONTENT replaced less-favorable tenant-facing copy. */
  readonly legalFloorClamped: boolean;
}

const ORDINARY_TERM =
  'Běžné zboží: při storno / no-show plný refund (dle podmínek při zaplacení).';
const PREPARED_TERM =
  'Připravované / časově citlivé: plný refund do zahájení přípravy objednávky; poté bez automatického refundu.';

/** Favorability rank — lower = less favorable to the customer (mirrors PlatformLegalFloorGuard). */
const FAVORABILITY_RANK: Record<
  'NO_AUTOMATIC_REFUND' | 'FULL_UNTIL_PREP_STARTED' | 'FULL_REFUND',
  number
> = {
  NO_AUTOMATIC_REFUND: 0,
  FULL_UNTIL_PREP_STARTED: 1,
  FULL_REFUND: 2,
};

function impliedRuleForClass(
  fulfillmentClass: MixedCartFulfillmentClass
): 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED' {
  return fulfillmentClass === 'ORDINARY' ? 'FULL_REFUND' : 'FULL_UNTIL_PREP_STARTED';
}

function resolveStrictestFloorCopy(
  floors: readonly MixedCartLegalFloor[],
  cartRule: 'FULL_REFUND' | 'FULL_UNTIL_PREP_STARTED'
): string | null {
  let strictestMin: MixedCartLegalFloor | null = null;
  for (const floor of floors) {
    const min = floor.minAutomaticRefundRule;
    if (min == null) {
      continue;
    }
    if (
      strictestMin === null ||
      FAVORABILITY_RANK[min] >
        FAVORABILITY_RANK[strictestMin.minAutomaticRefundRule as typeof min]
    ) {
      strictestMin = floor;
    }
  }
  if (strictestMin?.minAutomaticRefundRule == null) {
    return null;
  }
  const minRule = strictestMin.minAutomaticRefundRule;
  if (FAVORABILITY_RANK[cartRule] < FAVORABILITY_RANK[minRule]) {
    const copy = strictestMin.payload?.customerFacingCopy;
    return typeof copy === 'string' && copy.length > 0 ? copy : null;
  }
  return null;
}

export function presentMixedCartRefundTerms(
  lines: readonly MixedCartLineTermInput[],
  legalFloors?: MixedCartLegalFloorsInput | null
): MixedCartRefundTermsViewModel {
  const ordinary = lines.filter((l) => l.fulfillmentClass === 'ORDINARY');
  const prepared = lines.filter((l) => l.fulfillmentClass === 'PREPARED_TIME_SENSITIVE');
  const isMixed = ordinary.length > 0 && prepared.length > 0;

  const lineNotes = lines.map((line) => ({
    productId: line.productId,
    productName: line.productName,
    termLabel: line.fulfillmentClass === 'ORDINARY' ? ORDINARY_TERM : PREPARED_TERM,
  }));

  let summary: string;
  if (lines.length === 0) {
    summary = '';
  } else if (isMixed) {
    summary =
      'Košík obsahuje položky s různými storno / refund podmínkami. Rozdíly jsou uvedeny u jednotlivých položek.';
  } else if (prepared.length > 0) {
    summary = PREPARED_TERM;
  } else {
    summary = ORDINARY_TERM;
  }

  let legalFloorClamped = false;
  const floors = legalFloors?.floors ?? [];
  if (floors.length > 0 && lines.length > 0) {
    // Homogeneous cart: compare implied class rule to floors.
    // Mixed cart already discloses per-line terms; clamp only when a single class under-floors.
    if (!isMixed) {
      const first = lines[0];
      if (first !== undefined) {
        const cartRule = impliedRuleForClass(first.fulfillmentClass);
        const floorCopy = resolveStrictestFloorCopy(floors, cartRule);
        if (floorCopy !== null) {
          summary = floorCopy;
          legalFloorClamped = true;
        }
      }
    }
  }

  return {
    isMixed,
    ordinaryCount: ordinary.length,
    preparedCount: prepared.length,
    summary,
    lineNotes,
    legalFloorClamped,
  };
}
